import type { DraftResponse, NeedsInputItem } from '../api/draft';

// A recurring series arrives as N identical needs-input cards and costs N
// identical decisions. Items reading as the same work collapse into one entry
// the user opens, checks, and attributes in a single pass.
//
// Source is part of the key, not just the subject: an Outlook meeting and a
// Freshdesk ticket that happen to share a title are unrelated work. An item
// with no source therefore never groups — source is null on a rehydrated draft
// whose event left the read, and unknown is not the same as equal.

export type NeedsInputEntry =
  | { kind: 'single'; key: string; item: NeedsInputItem }
  | { kind: 'group'; key: string; items: NeedsInputItem[] };

// The backend's sentinel for an event that no longer resolves. Every degraded
// item carries it, so it would collide them all into one meaningless group.
const DEGRADED = '(event not found)';

export function groupKeyFor(item: NeedsInputItem): string | null {
  const subject = (item.subject ?? '').trim().toLowerCase();
  if (!subject || subject === DEGRADED || !item.source) return null;
  return `${item.source}|${subject}`;
}

/**
 * Collapse same-source, same-subject items into groups, preserving the order
 * the items arrive in (a group takes its earliest member's position, so the
 * list stays a timeline).
 *
 * @param isDismissed a dismissed card is a state the user set explicitly —
 *   it stays a single so it never hides inside a collapsed group.
 * @param ungrouped group keys the user has dissolved; they render as singles.
 */
export function buildNeedsInputEntries(
  items: readonly NeedsInputItem[],
  isDismissed: (eventId: string) => boolean,
  ungrouped: ReadonlySet<string>,
): NeedsInputEntry[] {
  const buckets = new Map<string, NeedsInputItem[]>();
  for (const item of items) {
    const key = groupKeyFor(item);
    if (key === null || isDismissed(item.eventId) || ungrouped.has(key)) continue;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  const entries: NeedsInputEntry[] = [];
  const emitted = new Set<string>();
  for (const item of items) {
    const key = groupKeyFor(item);
    const bucket = key === null ? undefined : buckets.get(key);
    // A group of one is a lie about the content — it renders as a plain card.
    // A dismissed item is excluded from the bucket above but still has to reach
    // the list, or its undo goes with it.
    if (key === null || bucket === undefined || bucket.length < 2 || isDismissed(item.eventId)) {
      entries.push({ kind: 'single', key: item.eventId, item });
      continue;
    }
    if (emitted.has(key)) continue;
    emitted.add(key);
    entries.push({ kind: 'group', key, items: bucket });
  }
  return entries;
}

export function groupHours(items: readonly NeedsInputItem[]): number {
  return Math.round(items.reduce((sum, i) => sum + (i.hours ?? 0), 0) * 100) / 100;
}

// One reason on the group only when every member gives the same one —
// summarising disagreement would invent an agreement that isn't there.
export function sharedReason(items: readonly NeedsInputItem[]): string | null {
  const first = items[0]?.reason;
  if (!first) return null;
  return items.every(i => i.reason === first) ? first : null;
}

/**
 * The needs-input items still waiting on the user, dismissed ones included (a
 * dismissed card stays visible so the dismissal can be undone). Feeds both the
 * drawer's Pending section and the Triage button's count.
 */
export function queuedNeedsInput(
  draft: DraftResponse | null,
  effectiveDraft: DraftResponse | null,
  isConfirmed: (eventId: string) => boolean,
): NeedsInputItem[] {
  // The overlay is the user's last word about an event, so where both drafts
  // carry an item the reconciled one wins. "Add again" overrules the written
  // mark there and not on the engine draft, and reading the engine's copy left
  // the card filtered out of this queue while the Published feed had already
  // released it — in no feed at all (#6494).
  const effectiveById = new Map(
    (effectiveDraft?.needsInput ?? []).map(i => [i.eventId, i] as const));
  const engineQueued = (draft?.needsInput ?? []).map(i => effectiveById.get(i.eventId) ?? i);
  const engineIds = new Set(engineQueued.map(i => i.eventId));
  // An event the user unassigned from a cell is queued by the reconciliation,
  // not by the engine, so it only appears on the effective draft.
  const requeued = (effectiveDraft?.needsInput ?? []).filter(i => !engineIds.has(i.eventId));
  return [...engineQueued, ...requeued]
    .filter(i => !isConfirmed(i.eventId))
    // Work this app already wrote is not waiting on the user, whatever shape it
    // came back in. It belongs in the Published feed, not the queue (#6487) —
    // this is what a regenerate leaves behind once the overlay is cleared.
    .filter(i => !i.written);
}
